'use client';
import { fetchRelatedQueries } from '#pplx/frontend/apps/ask/queries/entry-queries/fetchRelatedQueries.js';
import { useAsyncRelatedQueriesStore } from '#pplx/frontend/apps/ask/stores/asyncRelatedQueriesStore.js';
import { streamEvents } from '#pplx/frontend/stream/events.js';
import { useEffect } from 'react';
/**
 * Async related-query delivery: when a turn completes with `async_rq_enabled`
 * (chips generated in the background, not embedded in the COMPLETED message),
 * fetch them from the SSE endpoint into the async related-queries store so they
 * render this session without a reload (overlaid at read time onto the entry).
 *
 * - Fires once per completion. Ask edits reuse an entry ID but mint a new
 *   frontend UUID, so each completed revision gets its own fetch.
 * - Keeps in-flight fetches alive when the user starts a new turn; previous
 *   entries are hidden by `useShouldShowRelated` (isLastResult).
 */
export function useAsyncRelatedQueries() {
    useEffect(() => {
        const fetched = new Set();
        const controllers = new Set();
        const currentCompletionByEntry = new Map();
        const inFlightByEntry = new Map();
        const offCompleted = streamEvents.on('completed', ({ message, stream }) => {
            const entryId = message?.backend_uuid;
            if (!entryId) {
                return;
            }
            const completionId = message.frontend_uuid ?? stream.id.description;
            const completionKey = `${entryId}:${completionId}`;
            const previousCompletion = currentCompletionByEntry.get(entryId);
            if (previousCompletion !== completionKey) {
                currentCompletionByEntry.set(entryId, completionKey);
                useAsyncRelatedQueriesStore.getState().clearRelatedQueries(entryId);
                inFlightByEntry.get(entryId)?.abort();
                inFlightByEntry.delete(entryId);
            }
            if (!message.async_rq_enabled || fetched.has(completionKey)) {
                return;
            }
            fetched.add(completionKey);
            const controller = new AbortController();
            controllers.add(controller);
            inFlightByEntry.set(entryId, controller);
            void fetchRelatedQueries({
                entryUUID: entryId,
                signal: controller.signal,
                reason: 'async-related-queries',
                onResult: (payload) => {
                    if (currentCompletionByEntry.get(entryId) !== completionKey) {
                        return;
                    }
                    useAsyncRelatedQueriesStore.getState().setRelatedQueries(entryId, payload);
                },
            })
                .catch(() => {
                // Best-effort: on abort/error the chips still surface on reload (DDB).
            })
                .finally(() => {
                controllers.delete(controller);
                if (inFlightByEntry.get(entryId) === controller) {
                    inFlightByEntry.delete(entryId);
                }
            });
        });
        return () => {
            offCompleted();
            controllers.forEach((controller) => controller.abort());
        };
    }, []);
}
//# sourceMappingURL=useAsyncRelatedQueries.js.map